import {Category, ErrorType} from '@vio/core/src/api-types/bovio/response/error'
import {ErrorResponse} from '@vio/core/src/booking/types/ErrorResponse'

/**
 * Shape of the error attached to a rejected RTK Query offer-check action
 * (see `fetchBaseQuery`). `data` is the parsed BoVio {@link ErrorResponse} when
 * the server returned a JSON body, or a plain string for transport/network
 * failures and non-JSON responses.
 */
type OfferCheckRejectedPayload =
  | {
      status?: number
      data?: ErrorResponse | string
    }
  | undefined

/**
 * BoVio error categories that represent a transient, retryable failure rather
 * than a real sold-out. Everything else (sold_out, payment/booking, price
 * mismatch, …) defaults to the sold-out experience.
 */
const NON_RECOVERABLE_CATEGORIES: ReadonlySet<string> = new Set([
  Category.SoldOut,
  Category.PriceMismatch
])

/**
 * Decides whether a rejected offer-check should be treated as a recoverable
 * site error (show a retry toast, keep the offer bookable) instead of marking
 * the offer sold out.
 *
 * Sold-out is the default. An error is recoverable only when:
 * 1. There is no confirmed BoVio response body — the request was blocked, the
 *    network failed, it timed out, or the server replied without a JSON body.
 * 2. BoVio explicitly flags the error as retryable (`details.recoverable`).
 * 3. The BoVio `details.category` is one of {@link RECOVERABLE_CATEGORIES}.
 *
 * @param payload - The `payload` of the rejected RTK Query offer-check action.
 * @returns `true` when the failure is recoverable, `false` for a true sold-out.
 */
export const isRecoverableOfferCheckError = (
  payload: OfferCheckRejectedPayload
): boolean => {
  const data = payload?.data

  // No confirmed BoVio response (blocked request, network failure, timeout,
  // server error without a JSON body, parse error) → recoverable site error.
  if (!data || typeof data === 'string') return true

  const {details, type} = data

  if (type === ErrorType.OfferExpired) return false
  if (details?.recoverable === true) return true

  if (!details || NON_RECOVERABLE_CATEGORIES.has(details.category)) return false
  return true
}
